import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useActivePortal } from '@/context/ActivePortalContext';

export interface AdminAttentionItem {
  item_id: string;
  kind: string;
  source: string;
  severity: string;
  title: string;
  subtitle: string;
  href: string;
  created_at: string;
}

interface AdminAttentionResult {
  items: AdminAttentionItem[];
  totalCount: number;
  producerInterestCount: number;
  placeSuggestionCount: number;
  isLoading: boolean;
}

/**
 * Fetches admin attention items from admin_attention_v1 view.
 * Only active when user is in admin portal context.
 */
export function useAdminAttention(): AdminAttentionResult {
  const { activePortal } = useActivePortal();
  const isAdmin = activePortal === 'admin';

  const { data: items = [], isLoading } = useQuery({
    queryKey: ['admin-attention-items'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('admin_attention_v1')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) {
        if (import.meta.env.DEV) {
          console.debug('[useAdminAttention] query error:', error.message);
        }
        return [];
      }

      if (import.meta.env.DEV) {
        console.debug(`[useAdminAttention] fetched ${data?.length ?? 0} items`);
      }

      return (data ?? []) as AdminAttentionItem[];
    },
    enabled: isAdmin,
    staleTime: 1000 * 60 * 2, // 2 min – Phase 1 stability: admin attention list doesn't need aggressive refresh
    refetchOnWindowFocus: false,
  });

  const producerInterestCount = items.filter(i => i.kind === 'producer_interest').length;
  const placeSuggestionCount = items.filter(i => i.kind === 'place_suggestion').length;

  return {
    items,
    totalCount: items.length,
    producerInterestCount,
    placeSuggestionCount,
    isLoading,
  };
}
